// FILE: apps/admin/src/pages/Games.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Games Control Center for Crash & Cases P&L, realized vs theoretical RTP, interactive Case Drop Weight & Price Editor with live EV/RTP calculation, and Crash house-edge tuning.
//   SCOPE: Games component.
//   DEPENDS: ../api.js, ../display-currency.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Games - Admin Games Control Center for Crash and Cases
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api, dt, num, rub } from '../api.js';
import { CompanionIcon } from '../components/Icons.js';
import { useDisplayCurrency } from '../display-currency.js';
import { useToast } from '../toast.js';

interface CaseLootItem {
  type: 'rub' | 'xp' | 'stars_discount' | 'brainrot';
  value: number | string;
  weight: number;
}

interface CaseDefDto {
  id: string;
  name: string;
  priceRub: number;
  priceGram?: number;
  variant: string;
  totalWeight: number;
  expectedRubKopecks: number;
  rtpPercent: number;
  loot: CaseLootItem[];
}

interface GamesOverviewResponse {
  crash: {
    houseEdgePercent: number;
    minBetRub: number;
    maxBetRub: number;
    totalRoundsCount: number;
    totalBetsCount: number;
    totalWageredKopecks: number;
    totalPayoutsKopecks: number;
    houseProfitKopecks: number;
    realizedRtpPercent: number;
    recentRounds: Array<{
      id: string;
      status: string;
      crashPoint: number | null;
      startedAt: string | null;
      crashedAt: string | null;
    }>;
  };
  cases: {
    totalOpeningsCount: number;
    totalRevenueKopecks: number;
    totalPayoutsKopecks: number;
    houseProfitKopecks: number;
    realizedRtpPercent: number;
    definitions: CaseDefDto[];
    recentOpenings: Array<{
      id: string;
      userId: string;
      telegramId: number | null;
      username: string | null;
      caseId: string;
      priceKopecks: number;
      rewardType: string;
      rewardValue: string;
      createdAt: string;
    }>;
  };
}

function computeDraftEvAndRtp(priceRub: number, loot: CaseLootItem[]) {
  // START_BLOCK_GAMES_COMPUTE_DRAFT_EV
  const totalWeight = loot.reduce((s, l) => s + Math.max(0, Number(l.weight || 0)), 0);
  if (totalWeight <= 0 || priceRub <= 0) {
    return { totalWeight: 0, evRub: 0, rtpPercent: 0 };
  }
  let evRub = 0;
  for (const item of loot) {
    const prob = Math.max(0, Number(item.weight || 0)) / totalWeight;
    if (item.type === 'rub') {
      evRub += prob * Number(item.value || 0);
    }
  }
  const rtpPercent = Number(((evRub / priceRub) * 100).toFixed(2));
  return { totalWeight, evRub: Number(evRub.toFixed(2)), rtpPercent };
  // END_BLOCK_GAMES_COMPUTE_DRAFT_EV
}

// START_CONTRACT: Games
//   PURPOSE: Render the Crash & Cases Control Center with live EV/RTP Case Drop Weight Editor and Crash settings.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/games, PUT /admin/games/cases/:id, and PUT /admin/games/crash
//   LINKS: M-ADMIN
// END_CONTRACT: Games
export function Games() {
  // START_BLOCK_GAMES
  const qc = useQueryClient();
  const toast = useToast();
  const { money } = useDisplayCurrency();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-games'],
    queryFn: () => api.get<GamesOverviewResponse>('/admin/games'),
    refetchInterval: 15000,
  });

  const [selectedCaseId, setSelectedCaseId] = useState<string>('shell');
  const [draftName, setDraftName] = useState('');
  const [draftPriceRub, setDraftPriceRub] = useState(49);
  const [draftPriceGram, setDraftPriceGram] = useState(49);
  const [draftLoot, setDraftLoot] = useState<CaseLootItem[]>([]);

  const [houseEdge, setHouseEdge] = useState('4');
  const [minBet, setMinBet] = useState('10');
  const [maxBet, setMaxBet] = useState('5000');

  useEffect(() => {
    if (!data) return;
    setHouseEdge(String(data.crash.houseEdgePercent));
    setMinBet(String(data.crash.minBetRub));
    setMaxBet(String(data.crash.maxBetRub));
    const c =
      data.cases.definitions.find((d) => d.id === selectedCaseId) ??
      data.cases.definitions[0];
    if (c) {
      setSelectedCaseId(c.id);
      setDraftName(c.name);
      setDraftPriceRub(c.priceRub);
      setDraftPriceGram(c.priceGram ?? c.priceRub);
      setDraftLoot(c.loot.map((l) => ({ ...l })));
    }
  }, [data, selectedCaseId]);

  const saveCaseMut = useMutation({
    mutationFn: () =>
      api.put(`/admin/games/cases/${selectedCaseId}`, {
        name: draftName,
        priceRub: Number(draftPriceRub),
        priceGram: Number(draftPriceGram),
        loot: draftLoot,
      }),
    onSuccess: () => {
      toast('Конфигурация кейса и веса дропа сохранены');
      void qc.invalidateQueries({ queryKey: ['admin-games'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения кейса'),
  });

  const saveCrashMut = useMutation({
    mutationFn: () =>
      api.put('/admin/games/crash', {
        houseEdgePercent: Number(houseEdge),
        minBetRub: Number(minBet),
        maxBetRub: Number(maxBet),
      }),
    onSuccess: () => {
      toast('Параметры игры Crash обновлены');
      void qc.invalidateQueries({ queryKey: ['admin-games'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения Crash'),
  });

  if (isLoading || !data) return <div className="muted">Загрузка игрового центра…</div>;

  const liveStats = computeDraftEvAndRtp(Number(draftPriceRub), draftLoot);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Игровой центр (Crash & Кейсы)</h1>
          <div className="page-subtitle">
            Управление математическим ожиданием (EV), RTP, весами выпадения предметов в кейсах и параметрами Crash
          </div>
        </div>
      </div>

      {/* Top P&L & RTP KPIs */}
      <div className="grid metrics" style={{ marginBottom: 20 }}>
        <div className="kpi-card purple">
          <div className="kpi-label">Профит казино (Crash)</div>
          <div className="kpi-value">{money(data.crash.houseProfitKopecks)}</div>
          <div className="kpi-sub">
            Ставок: {num(data.crash.totalBetsCount)} · Раундов: {num(data.crash.totalRoundsCount)}
          </div>
        </div>
        <div className="kpi-card cyan">
          <div className="kpi-label">Фактический RTP Crash</div>
          <div className="kpi-value">{data.crash.realizedRtpPercent}%</div>
          <div className="kpi-sub">
            Теоретический RTP: {(100 - data.crash.houseEdgePercent).toFixed(1)}%
          </div>
        </div>
        <div className="kpi-card amber">
          <div className="kpi-label">Профит кейсов</div>
          <div className="kpi-value">{money(data.cases.houseProfitKopecks)}</div>
          <div className="kpi-sub">
            Открыто кейсов: {num(data.cases.totalOpeningsCount)}
          </div>
        </div>
        <div className="kpi-card green">
          <div className="kpi-label">Фактический RTP Кейсов</div>
          <div className="kpi-value">{data.cases.realizedRtpPercent}%</div>
          <div className="kpi-sub">
            Выручка: {rub(data.cases.totalRevenueKopecks)} ₽ · Выплаты: {rub(data.cases.totalPayoutsKopecks)} ₽
          </div>
        </div>
      </div>

      {/* Interactive Case Drop Weight & Price Editor + Crash Config */}
      <div className="grid cols-2" style={{ marginBottom: 20, alignItems: 'start' }}>
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ margin: 0 }}>Симулятор и редактор кейсов (EV / RTP)</h2>
            <div className="tabs" style={{ marginBottom: 0 }}>
              {data.cases.definitions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`tab-btn ${selectedCaseId === c.id ? 'active' : ''}`}
                  onClick={() => setSelectedCaseId(c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid cols-3" style={{ gap: 10, marginBottom: 14 }}>
            <div>
              <label className="muted" style={{ fontSize: 11 }}>Название кейса</label>
              <input
                style={{ width: '100%' }}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
              />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 11 }}>Цена (₽ RUB)</label>
              <input
                type="number"
                style={{ width: '100%' }}
                value={draftPriceRub}
                onChange={(e) => setDraftPriceRub(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 11 }}>Цена (GRAM)</label>
              <input
                type="number"
                style={{ width: '100%' }}
                value={draftPriceGram}
                onChange={(e) => setDraftPriceGram(Number(e.target.value))}
              />
            </div>
          </div>

          <div
            className="card"
            style={{
              background: 'var(--bg-2)',
              padding: 12,
              marginBottom: 14,
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <div>
              <div className="muted" style={{ fontSize: 11 }}>Сумма весов (Weight)</div>
              <div className="mono" style={{ fontWeight: 700, fontSize: 16 }}>{liveStats.totalWeight}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>Мат. ожидание (EV ₽)</div>
              <div className="mono" style={{ fontWeight: 700, fontSize: 16 }}>{liveStats.evRub} ₽</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>Расчётный RTP</div>
              <div
                className="mono"
                style={{
                  fontWeight: 800,
                  fontSize: 16,
                  color: liveStats.rtpPercent > 95 ? 'var(--bad)' : 'var(--ok)',
                }}
              >
                {liveStats.rtpPercent}%
              </div>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Тип награды</th>
                  <th>Значение</th>
                  <th>Вес (Weight)</th>
                  <th>Шанс %</th>
                </tr>
              </thead>
              <tbody>
                {draftLoot.map((item, idx) => {
                  const chance =
                    liveStats.totalWeight > 0
                      ? ((Number(item.weight || 0) / liveStats.totalWeight) * 100).toFixed(1)
                      : '0.0';
                  return (
                    <tr key={idx}>
                      <td><span className="chip info">{item.type}</span></td>
                      <td className="mono">
                        {item.type === 'rub' ? (
                          `${item.value} ₽`
                        ) : item.type === 'xp' ? (
                          `${item.value} XP`
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CompanionIcon companionId={String(item.value)} size={16} />
                            <span>{item.value}</span>
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          style={{ width: 90, padding: '5px 8px' }}
                          value={item.weight}
                          onChange={(e) => {
                            const next = [...draftLoot];
                            next[idx] = { ...item, weight: Math.max(0, Number(e.target.value)) };
                            setDraftLoot(next);
                          }}
                        />
                      </td>
                      <td className="mono muted">{chance}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button
              type="button"
              className="btn primary"
              disabled={saveCaseMut.isPending}
              onClick={() => saveCaseMut.mutate()}
            >
              {saveCaseMut.isPending ? 'Сохранение…' : 'Сохранить таблицу дропа'}
            </button>
          </div>
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Настройки мини-игры Crash</h2>
          <p className="muted" style={{ fontSize: 13 }}>
            Конфигурация преимущества платформы (House Edge) и лимитов ставок в реальном времени.
          </p>
          <div className="field">
            <div>
              <div style={{ fontWeight: 600 }}>House Edge (%)</div>
              <div className="meta">Доля казино в генераторе множителя (1–25%)</div>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <input
                style={{ flex: 1, minWidth: 0 }}
                value={houseEdge}
                onChange={(e) => setHouseEdge(e.target.value)}
              />
              <span className="muted">%</span>
            </div>
          </div>
          <div className="field">
            <div>
              <div style={{ fontWeight: 600 }}>Минимальная ставка (₽)</div>
              <div className="meta">Нижний порог входа в раунд</div>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <input
                style={{ flex: 1, minWidth: 0 }}
                value={minBet}
                onChange={(e) => setMinBet(e.target.value)}
              />
              <span className="muted">₽</span>
            </div>
          </div>
          <div className="field">
            <div>
              <div style={{ fontWeight: 600 }}>Максимальная ставка (₽)</div>
              <div className="meta">Верхний лимит ставки на один раунд</div>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <input
                style={{ flex: 1, minWidth: 0 }}
                value={maxBet}
                onChange={(e) => setMaxBet(e.target.value)}
              />
              <span className="muted">₽</span>
            </div>
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button
              type="button"
              className="btn primary"
              disabled={saveCrashMut.isPending}
              onClick={() => saveCrashMut.mutate()}
            >
              Применить настройки Crash
            </button>
          </div>

          <h2 style={{ marginTop: 22 }}>Последние открытия кейсов</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Кейс</th>
                  <th>Выигрыш</th>
                  <th>Время</th>
                </tr>
              </thead>
              <tbody>
                {data.cases.recentOpenings.slice(0, 8).map((op) => (
                  <tr key={op.id}>
                    <td className="mono">{op.username ? `@${op.username}` : `#${op.telegramId ?? '—'}`}</td>
                    <td>{op.caseId}</td>
                    <td>
                      <span className="chip info">{op.rewardType}</span> <strong>{op.rewardValue}</strong>
                    </td>
                    <td className="muted">{dt(op.createdAt)}</td>
                  </tr>
                ))}
                {!data.cases.recentOpenings.length && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 18 }}>
                      Открытий кейсов пока нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
  // END_BLOCK_GAMES
}
